/**
 * Shared display-name helpers for the viewer UI (v0.7 F1/F3 polish).
 *
 * Two wire flags surface model anonymity that the UI must present nicely
 * instead of leaking raw sentinels:
 *
 *   - `Interface.anonymous` (§4.13.2): a synthesized handler standing in
 *     for an unspecified callee interface. Core mints its `name` as the
 *     literal `(anonymous)` sentinel — we show `(unspecified)` instead,
 *     styled muted/italic at sites that have styling.
 *
 *   - `Module.unnamed` (§4.14): the module's name was DERIVED from its
 *     type rather than authored. The name is real, but a subtle "derived"
 *     cue tells the reader it wasn't spelled out.
 */

/** The raw sentinel name core mints for an anonymous callee handler
 *  (§4.13.2). Duplicated here rather than imported from core to honour the
 *  app-shell package boundary; kept in one place so callers don't inline
 *  the magic string. */
export const ANONYMOUS_INTERFACE_NAME = "(anonymous)";

/** Friendly placeholder shown in place of the raw `(anonymous)` sentinel. */
export const UNSPECIFIED_INTERFACE_LABEL = "(unspecified)";

/** True when an interface is the synthesized anonymous handler — either by
 *  the `anonymous` wire flag or, defensively, by the raw sentinel name. */
export function isAnonymousInterface(iface: {
  readonly name?: string;
  readonly anonymous?: boolean;
}): boolean {
  return iface.anonymous === true || iface.name === ANONYMOUS_INTERFACE_NAME;
}

/** Plain-text display name for an interface: the friendly `(unspecified)`
 *  placeholder for an anonymous handler (§4.13.2), else the authored name. */
export function interfaceDisplayName(iface: {
  readonly name: string;
  readonly anonymous?: boolean;
}): string {
  return isAnonymousInterface(iface) ? UNSPECIFIED_INTERFACE_LABEL : iface.name;
}

/** Map a raw interface-name string (e.g. the last segment of a process
 *  step's qualified target, where we don't hold the Interface object) to
 *  its display form, swapping the `(anonymous)` sentinel for `(unspecified)`. */
export function displayInterfaceNameString(name: string): string {
  return name === ANONYMOUS_INTERFACE_NAME ? UNSPECIFIED_INTERFACE_LABEL : name;
}
